interface TriggerI {
  type: TriggerType;
  toJs(): any;
}

export enum AgentStatusType {
  Away = 'AWAY',
  Offline = 'OFFLINE',
}

export enum TriggerType {
  AgentStatusUpdate = 'AGENT_STATUS_UPDATE',
  AnswerUsed = 'ANSWER_USED',
  ConversationDueIn = 'CONVERSATION_DUE_IN',
  IncomingCommunication = 'INCOMING_COMMUNICATION',
  SmartMatch = 'SMART_MATCH',
  TaskDueIn = 'TASK_DUE_IN',
  TopicsAdded = 'TOPICS_ADDED',
}

export enum DueStatus {
  DueIn = 'DUE_IN',
  Overdue = 'OVERDUE',
  OverdueBy = 'OVERDUE_BY',
}

export class DueValue {
  status: DueStatus;
  minutes: number;

  constructor(value: number) {
    if (value > 0) {
      this.status = DueStatus.DueIn;
      this.minutes = value;
      return;
    }

    if (value < 0) {
      this.status = DueStatus.OverdueBy;
      this.minutes = -value;
      return;
    }

    this.status = DueStatus.Overdue;
    this.minutes = 0;
  }

  setMinutes(minutes: number) {
    this.minutes = minutes;
  }

  setStatus(status: DueStatus) {
    this.status = status;
    switch (status) {
      case DueStatus.DueIn:
      case DueStatus.OverdueBy:
        if (this.minutes === 0) {
          // minutes value of 0 is not valid with these status types
          this.minutes = 1;
        }
        break;
      default:
        this.minutes = 0;
    }
  }

  toJs() {
    switch (this.status) {
      case DueStatus.DueIn:
        return this.minutes;
      case DueStatus.OverdueBy:
        return -this.minutes;
      default:
        return 0;
    }
  }
}

export class AgentStatusUpdateTrigger implements TriggerI {
  type: TriggerType;
  value: AgentStatusType;

  // eslint-disable-next-line
  constructor(value = AgentStatusType.Offline) {
    this.type = TriggerType.AgentStatusUpdate;
    this.value = value;
  }

  setValue(value: AgentStatusType) {
    this.value = value;
  }

  toJs() {
    return JSON.parse(JSON.stringify(this));
  }
}

export class AnswerUsedTrigger implements TriggerI {
  type: TriggerType;

  constructor() {
    this.type = TriggerType.AnswerUsed;
  }

  toJs() {
    return JSON.parse(JSON.stringify(this));
  }
}

export abstract class DueInTrigger implements TriggerI {
  type: TriggerType;
  value: DueValue;

  constructor({ value, type }: { value: number; type: TriggerType }) {
    this.type = type;
    this.value = new DueValue(value);
  }

  setValue(value: DueValue) {
    this.value = value;
  }

  toJs() {
    return { ...this, value: this.value.toJs() };
  }
}

export class ConversationDueInTrigger extends DueInTrigger {
  constructor(value = 0) {
    super({ value, type: TriggerType.ConversationDueIn });
  }
}

export class IncomingCommunicationTrigger implements TriggerI {
  type: TriggerType;

  constructor() {
    this.type = TriggerType.IncomingCommunication;
  }

  toJs() {
    return JSON.parse(JSON.stringify(this));
  }
}

export class SmartMatchTrigger implements TriggerI {
  type: TriggerType;

  constructor() {
    this.type = TriggerType.SmartMatch;
  }

  toJs() {
    return JSON.parse(JSON.stringify(this));
  }
}

export class TaskDueInTrigger extends DueInTrigger {
  constructor(value = 0) {
    super({ value, type: TriggerType.TaskDueIn });
  }
}

export class TopicsAddedTrigger implements TriggerI {
  type: TriggerType;

  constructor() {
    this.type = TriggerType.TopicsAdded;
  }

  toJs() {
    return JSON.parse(JSON.stringify(this));
  }
}

export function triggerFromDto(dto: { type: TriggerType; value?: number | AgentStatusType }): Trigger {
  switch (dto.type) {
    case TriggerType.AgentStatusUpdate:
      return new AgentStatusUpdateTrigger(dto.value as AgentStatusType);
    case TriggerType.AnswerUsed:
      return new AnswerUsedTrigger();
    case TriggerType.ConversationDueIn:
      return new ConversationDueInTrigger(dto.value as number);
    case TriggerType.IncomingCommunication:
      return new IncomingCommunicationTrigger();
    case TriggerType.SmartMatch:
      return new SmartMatchTrigger();
    case TriggerType.TaskDueIn:
      return new TaskDueInTrigger(dto.value as number);
    case TriggerType.TopicsAdded:
      return new TopicsAddedTrigger();
    default:
      return {} as Trigger;
  }
}

export type Trigger =
  | AgentStatusUpdateTrigger
  | AnswerUsedTrigger
  | DueInTrigger
  | IncomingCommunicationTrigger
  | SmartMatchTrigger
  | TopicsAddedTrigger;

export default Trigger;
